/**
 * User-managed, reusable library of saved 阴影预设 (Shadow Presets) for the
 * Shadow Editor. Purely a frontend/browser concern (localStorage) — each
 * saved preset captures the *entire* `ShadowParams` state so re-applying it
 * later restores every slider exactly, and is independent from any single
 * chain's own persisted `shadow_params_json`.
 */
import type { ShadowParams } from './shadowRender'

export interface SavedShadowPreset {
  id: string
  name: string
  /** Small square PNG data URL snapshot of the canvas at save time. */
  thumbnail: string
  params: ShadowParams
  createdAt: number
}

const STORAGE_KEY = 'pic2content.shadowPresetLibrary.v1'
const DEFAULT_NAME_RE = /^(?:\u9634\u5f71\u9884\u8bbe|Shadow preset)\s*(\d+)$/i

export function nextDefaultPresetName(
  existing: SavedShadowPreset[],
  format?: (n: number) => string,
): string {
  let maxN = 0
  for (const p of existing) {
    const m = DEFAULT_NAME_RE.exec(p.name.trim())
    if (m) maxN = Math.max(maxN, parseInt(m[1], 10))
  }
  const n = maxN + 1
  return format ? format(n) : `Shadow preset ${n}`
}

function readRaw(): SavedShadowPreset[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter((p) => p && typeof p.id === 'string' && p.params)
  } catch {
    return []
  }
}

function writeRaw(list: SavedShadowPreset[]) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list))
  } catch {
    // Storage full/unavailable — presets simply won't persist across reloads.
  }
}

/** Presets are shown newest-first so a freshly-saved preset appears at the
 * very top of the left panel immediately (spec: 保存成功立即加入左侧顶部). */
export function loadPresetLibrary(): SavedShadowPreset[] {
  return readRaw().sort((a, b) => b.createdAt - a.createdAt)
}

/** Best-effort lookup for chains/templates saved before `sourcePresetId`
 * existed, which only recorded the preset's display name at save time
 * (spec 二.7: "如果没有 ID，则兼容名称匹配"). Only ever used once on load —
 * callers must persist the resolved id on the next save, never repeat this
 * lookup on every render. */
export function findPresetIdByName(existing: SavedShadowPreset[], name: string | undefined): string | null {
  if (!name) return null
  const trimmed = name.trim()
  if (!trimmed) return null
  const match = existing.find((p) => p.name.trim() === trimmed)
  return match ? match.id : null
}

export function savePresetToLibrary(name: string, params: ShadowParams, thumbnail: string): SavedShadowPreset[] {
  const list = readRaw()
  const entry: SavedShadowPreset = {
    id: `sp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    name: name.trim() || nextDefaultPresetName(list),
    thumbnail,
    params: JSON.parse(JSON.stringify(params)),
    createdAt: Date.now(),
  }
  list.push(entry)
  writeRaw(list)
  return loadPresetLibrary()
}

export function deletePresetFromLibrary(id: string): SavedShadowPreset[] {
  const list = readRaw().filter((p) => p.id !== id)
  writeRaw(list)
  return loadPresetLibrary()
}

/** Downscales the live editor canvas into a small thumbnail data URL for
 * the preset list — never the full-resolution canvas. */
export function makePresetThumbnail(sourceCanvas: HTMLCanvasElement, size = 96): string {
  const c = document.createElement('canvas')
  c.width = size
  c.height = size
  const ctx = c.getContext('2d')!
  const sw = sourceCanvas.width
  const sh = sourceCanvas.height
  const scale = Math.min(size / sw, size / sh)
  const dw = sw * scale
  const dh = sh * scale
  ctx.fillStyle = '#f4f5f7'
  ctx.fillRect(0, 0, size, size)
  ctx.drawImage(sourceCanvas, (size - dw) / 2, (size - dh) / 2, dw, dh)
  return c.toDataURL('image/jpeg', 0.72)
}
